// Advisory analysis of the actual capture audio. The server audio gate remains authoritative.
export const createGuidedAudioAnalysis = ({ stream, onSample }) => {
  let context;
  let source;
  let analyser;
  let timer;
  let disposed = false;
  let level = 0;
  let silenceSince = null;
  let lowSince = null;
  let loudSince = null;
  let clipWindowStartedAt = null;
  const history = [];
  const clippedHistory = [];
  const track = stream.getAudioTracks()[0];
  const unavailable = () => onSample({ level: 0, key: 'microphoneAnalysisUnavailable' });
  try {
    const Context = window.AudioContext || window.webkitAudioContext;
    if (!Context || !track) throw new Error('Audio analysis unavailable');
    context = new Context();
    source = context.createMediaStreamSource(new MediaStream([track]));
    analyser = context.createAnalyser();
    analyser.fftSize = 2048;
    source.connect(analyser);
  } catch {
    source?.disconnect();
    void context?.close().catch(() => undefined);
    unavailable();
    return { resume: async () => {}, dispose() { disposed = true; } };
  }
  const samples = new Float32Array(analyser.fftSize);
  const spectrum = new Float32Array(analyser.frequencyBinCount);
  const sample = () => {
    if (disposed) return;
    if (track.readyState !== 'live' || track.muted || !track.enabled) {
      silenceSince = null;
      lowSince = null;
      loudSince = null;
      clipWindowStartedAt = null;
      history.length = 0;
      clippedHistory.length = 0;
      onSample({ level: 0, key: 'microphoneDisconnected' });
      return;
    }
    if (context.state !== 'running') {
      silenceSince = null;
      lowSince = null;
      loudSince = null;
      clipWindowStartedAt = null;
      history.length = 0;
      clippedHistory.length = 0;
      unavailable();
      return;
    }
    const now = performance.now();
    clipWindowStartedAt ??= now;
    analyser.getFloatTimeDomainData(samples);
    let sum = 0;
    let clipped = 0;
    let peak = 0;
    for (let index = 0; index < samples.length; index += 1) {
      const value = samples[index];
      sum += value * value;
      peak = Math.max(peak, Math.abs(value));
      if (Math.abs(value) >= 0.98) clipped += 1;
    }
    const rms = Math.sqrt(sum / samples.length);
    const db = 20 * Math.log10(Math.max(rms, 1e-6));
    level += 0.2 * (Math.max(0, Math.min(100, (db + 60) / 60 * 100)) - level);
    silenceSince = db < -60 ? (silenceSince ?? now) : null;
    lowSince = db >= -60 && db < -35 ? (lowSince ?? now) : null;
    // Sustained near-full-scale RMS needs headroom even without hard-clipped samples.
    loudSince = db > -6 ? (loudSince ?? now) : null;
    clippedHistory.push({ now, clipped });
    while (clippedHistory.length && clippedHistory[0].now < now - 1000) clippedHistory.shift();
    const clipRatio = clippedHistory.reduce((total, entry) => total + entry.clipped, 0)
      / (clippedHistory.length * samples.length);
    analyser.getFloatFrequencyData(spectrum);
    const lowBin = Math.max(1, Math.ceil(300 * analyser.fftSize / context.sampleRate));
    const highBin = Math.min(spectrum.length - 1, Math.floor(6000 * analyser.fftSize / context.sampleRate));
    let powerSum = 0;
    let logSum = 0;
    for (let index = lowBin; index <= highBin; index += 1) {
      const power = Math.max(1e-12, 10 ** (spectrum[index] / 10));
      powerSum += power;
      logSum += Math.log(power);
    }
    const count = highBin - lowBin + 1;
    const flatness = count > 0 ? Math.exp(logSum / count) / (powerSum / count) : 0;
    history.push({ now, rms, noisy: flatness > 0.6 && db > -40 });
    while (history.length && history[0].now < now - 3100) history.shift();
    const mean = history.reduce((total, entry) => total + entry.rms, 0) / history.length;
    const deviation = Math.sqrt(history.reduce((total, entry) => total + (entry.rms - mean) ** 2, 0) / history.length);
    const noise = now - history[0].now >= 3000 && history.every(entry => entry.noisy)
      && deviation / Math.max(mean, 1e-6) < 0.2;
    let key = null;
    if (silenceSince !== null && now - silenceSince >= 3000) key = 'microphoneNoInput';
    else if ((now - clipWindowStartedAt >= 1000 && clipRatio > 0.01)
      || (loudSince !== null && now - loudSince >= 1000)) key = 'microphoneClipping';
    else if (lowSince !== null && now - lowSince >= 2000) key = 'microphoneTooQuiet';
    else if (noise) key = 'possibleBackgroundNoise';
    onSample({ level: Math.round(level), key, rms, peak, dbfs: db });
  };
  const resume = async () => {
    if (disposed) return;
    try {
      if (context.state === 'suspended' || context.state === 'interrupted') {
        await context.resume();
        sample();
      }
    } catch {
      if (!disposed) unavailable();
    }
  };
  timer = window.setInterval(sample, 100);
  sample();
  // Permission acquisition can complete after the preview gesture. Try now; callers
  // can retry resume synchronously from the next explicit preview/record gesture.
  void resume();
  return {
    resume,
    dispose() {
      disposed = true;
      window.clearInterval(timer);
      source.disconnect();
      analyser.disconnect();
      void context.close().catch(() => undefined);
    },
  };
};
